import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import styles from "./SignIn.module.css";
import { auth, RequestFailed } from "../../lib/api.js";
import { keys } from "../../lib/queries.js";
import { Alert, Button, Card, Field, Input, Logo, Stack, Text } from "../../ui/index.js";

type Mode = "sign-in" | "sign-up" | "forgot";

function IconArrowLeft() {
  return (
    <svg
      className={styles.backIcon}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
    >
      <path d="M20 12H4m0 0 6-6m-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const TITLES: Record<Mode, string> = {
  "sign-in": "Sign in",
  "sign-up": "Create an account",
  forgot: "Reset your password",
};

/**
 * The one screen that exists outside the household.
 *
 * Sign-up is here even though production runs invite-only, because the server
 * is the thing that enforces that — a client that hides the form is a
 * preference, not a policy, and the server answers the same either way.
 */
export function SignIn() {
  const client = useQueryClient();
  const [mode, setMode] = useState<Mode>("sign-in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setSent(null);

    try {
      if (mode === "forgot") {
        await auth("request-password-reset", { email, redirectTo: "/reset-password" });
        // Deliberately the same answer whether or not the address is known:
        // otherwise this form is a way to test who has an account.
        setSent("If that address has an account, a reset link is on its way.");
        return;
      }

      if (mode === "sign-up") {
        await auth("sign-up/email", { email, password, name: name || email });
        setSent("Check your email to confirm the address, then sign in.");
        setMode("sign-in");
        return;
      }

      await auth("sign-in/email", { email, password });
      await client.invalidateQueries({ queryKey: keys.me });
    } catch (failure) {
      setError(
        failure instanceof RequestFailed ? failure.message : "That did not work. Try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.page}>
      <Link to="/" className={styles.back}>
        <IconArrowLeft />
        Back
      </Link>

      <Stack gap={5} className={styles.panel}>
        <Stack gap={2}>
          <div className={styles.brand}>
            <Logo className={styles.mark} />
            <span className={styles.brandName}>Squirrel</span>
          </div>
          <Text className={styles.subtitle} tone="secondary" size="2xl">
            Household finances in a nutshell
          </Text>
        </Stack>

        <Card title={TITLES[mode]}>
          <form onSubmit={submit}>
            <Stack gap={4}>
              {error && <Alert tone="danger">{error}</Alert>}
              {sent && <Alert tone="positive">{sent}</Alert>}

              {mode === "sign-up" && (
                <Field label="Your name">
                  {(props) => (
                    <Input
                      {...props}
                      autoComplete="name"
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                    />
                  )}
                </Field>
              )}

              <Field label="Email">
                {(props) => (
                  <Input
                    {...props}
                    type="email"
                    inputMode="email"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                )}
              </Field>

              {mode !== "forgot" && (
                <Field
                  label="Password"
                  hint={mode === "sign-up" ? "At least 12 characters." : undefined}
                >
                  {(props) => (
                    <Input
                      {...props}
                      type="password"
                      autoComplete={mode === "sign-up" ? "new-password" : "current-password"}
                      required
                      minLength={mode === "sign-up" ? 12 : undefined}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                  )}
                </Field>
              )}

              <Button type="submit" variant="primary" fullWidth loading={busy}>
                {TITLES[mode]}
              </Button>
            </Stack>
          </form>
        </Card>

        <Stack gap={2} className={styles.links}>
          {mode !== "sign-in" && (
            <Button variant="ghost" size="sm" onClick={() => setMode("sign-in")}>
              Back to sign in
            </Button>
          )}
          {mode === "sign-in" && (
            <>
              <Button variant="ghost" size="sm" onClick={() => setMode("forgot")}>
                Forgotten your password?
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setMode("sign-up")}>
                Create an account
              </Button>
            </>
          )}
        </Stack>
      </Stack>
    </div>
  );
}
